import type {
  Automation,
  AutomationChannel,
  AutomationOptions,
  Channel,
  ChannelUnavailableReason
} from "../../SettingsAutomation/types/automation.types.ts"

export const AUTOMATION_CHANNELS: readonly AutomationChannel[] = [
  "EMAIL",
  "SMS",
  "EMAIL_AND_SMS"
]

export const parseChannel = (
  value: string | null | undefined
): AutomationChannel | null => {
  const upper = (value ?? "").toUpperCase()
  return AUTOMATION_CHANNELS.find((channel) => channel === upper) ?? null
}

export const sendsEmail = (channel: AutomationChannel | null) =>
  channel === "EMAIL" || channel === "EMAIL_AND_SMS"

export const sendsSms = (channel: AutomationChannel | null) =>
  channel === "SMS" || channel === "EMAIL_AND_SMS"

/** The single channels a choice sends on, email first. */
export const channelPartsOf = (channel: AutomationChannel | null): Channel[] =>
  [
    sendsEmail(channel) ? ("EMAIL" as const) : null,
    sendsSms(channel) ? ("SMS" as const) : null
  ].filter((part): part is Channel => part !== null)

const REASON_OF: Record<Channel, ChannelUnavailableReason> = {
  EMAIL: "email_not_configured",
  SMS: "sms_not_configured"
}

export interface ChannelReadiness {
  available: boolean
  /** Why it cannot be used; for Both, the first part that is not ready. */
  reasons: ChannelUnavailableReason[]
}

export type ReadinessMap = Record<AutomationChannel, ChannelReadiness>

/**
 * Whether each channel can be used, from the options' readiness. A server
 * that does not list Email and SMS together yet makes it as ready as its two
 * parts; a channel it does not list at all counts as not configured.
 */
export const channelReadiness = (options: AutomationOptions): ReadinessMap => {
  const entry = (channel: AutomationChannel) =>
    options.channels.find((item) => (item.value ?? item.key) === channel)
  const single = (channel: Channel): ChannelReadiness => {
    const item = entry(channel)
    return item?.status === "available"
      ? { available: true, reasons: [] }
      : { available: false, reasons: [item?.reason ?? REASON_OF[channel]] }
  }
  const email = single("EMAIL")
  const sms = single("SMS")
  const both = entry("EMAIL_AND_SMS")
  const partReasons = [...email.reasons, ...sms.reasons]
  return {
    EMAIL: email,
    SMS: sms,
    EMAIL_AND_SMS: both
      ? both.status === "available"
        ? { available: true, reasons: [] }
        : {
            available: false,
            reasons: partReasons.length
              ? partReasons
              : [both.reason ?? "sms_not_configured"]
          }
      : { available: email.available && sms.available, reasons: partReasons }
  }
}

export interface ChannelWarning {
  channel: Channel
  reason: ChannelUnavailableReason
}

const warningOf = (reason: ChannelUnavailableReason): ChannelWarning => ({
  channel: reason === "email_not_configured" ? "EMAIL" : "SMS",
  reason
})

/**
 * What stops the chosen channel from sending: the parts the options say are
 * not configured, plus whatever the saved record reports for the same channel.
 */
export const channelWarningsFor = (
  channel: AutomationChannel | null,
  readiness: ReadinessMap | null,
  saved?: Pick<Automation, "channel" | "channel_warnings"> | null
): ChannelWarning[] => {
  if (!channel) return []
  const reasons = new Set<ChannelUnavailableReason>(
    readiness?.[channel].reasons ?? []
  )
  if (saved && parseChannel(saved.channel) === channel)
    (saved.channel_warnings ?? []).forEach((warning) =>
      reasons.add(warning.reason)
    )
  return [...reasons].map(warningOf)
}

/** The record's own warnings, as the list shows them. */
export const recordWarnings = (automation: Automation): ChannelWarning[] =>
  (automation.channel_warnings ?? []).map((warning) =>
    warningOf(warning.reason)
  )
